import * as Sentry from '@sentry/react';

const { VITE_API_DEV_HOST } = process.env;

const ALLOWED_IMAGE_TYPES = [ 'image/png', 'image/jpeg', 'image/webp' ];
const MAX_IMAGE_SIZE = 15 * 1024 * 1024;

const OPTIMIZE_LONG_SIDES = [ 2048, 1536, 1024 ];
const OPTIMIZE_QUALITIES = [ 0.9, 0.85, 0.8, 0.75 ];

const stripExtension = name => (name || '').replace(/\.[^.]+$/, '');

const loadBitmap = async file => {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file);
    } catch (_) {
      // fall through to Image element fallback
    }
  }

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not decode the image.'));
    };
    img.src = url;
  });
};

const closeBitmap = bitmap => {
  if (bitmap && typeof bitmap.close === 'function') bitmap.close();
};

const drawScaledCanvas = (bitmap, maxLongSide) => {
  const width = bitmap.width || bitmap.naturalWidth;
  const height = bitmap.height || bitmap.naturalHeight;

  if (!width || !height) throw new Error('Could not read image dimensions.');

  const longSide = Math.max(width, height);
  const scale = longSide > maxLongSide ? maxLongSide / longSide : 1;

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));

  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

  return canvas;
};

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(blob => {
      if (blob) resolve(blob);
      else reject(new Error('Failed to encode optimized image.'));
    }, type, quality);
  });

export const optimizeImageBeforeUpload = async file => {
  if (!file) throw new Error('No image provided.');
  if (file.size <= MAX_IMAGE_SIZE) return file;

  const bitmap = await loadBitmap(file);

  try {
    const baseName = stripExtension(file.name) || 'image';

    for (const longSide of OPTIMIZE_LONG_SIDES) {
      const canvas = drawScaledCanvas(bitmap, longSide);

      for (const quality of OPTIMIZE_QUALITIES) {
        const blob = await canvasToBlob(canvas, 'image/webp', quality);

        if (blob.size <= MAX_IMAGE_SIZE) {
          return new File([ blob ], `${baseName}.webp`, { type: 'image/webp' });
        }
      }
    }
  } finally {
    closeBitmap(bitmap);
  }

  throw new Error('Image is too large to optimize under 15 MB. Please try a smaller image.');
};

export const urlToImageFile = async (url, baseName = 'image') => {
  if (!url) return null;

  const res = await fetch(url);
  if (!res.ok) throw new Error('Could not read the current image.');

  const blob = await res.blob();
  const type = blob.type && blob.type.startsWith('image/') ? blob.type : 'image/png';
  const ext = type === 'image/jpeg' ? 'jpg' : type.split('/')[1] || 'png';
  const safeName = `${stripExtension(baseName) || 'image'}.${ext}`;

  return new File([ blob ], safeName, { type });
};

export const removeImageBackground = async ({ file, token }) => {
  if (!file) {
    throw new Error('Please upload an image first.');
  }

  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    throw new Error('Unsupported file type. Please use PNG, JPG, JPEG, or WEBP.');
  }

  const uploadFile = await optimizeImageBeforeUpload(file);

  const fd = new FormData();
  fd.append('image', uploadFile);

  const res = await fetch(`${VITE_API_DEV_HOST}/api/ai/remove-background`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: fd,
  });

  if (!res.ok) {
    let data = null;
    try {
      data = await res.json();
    } catch (_) {
      // response was not JSON
    }

    if (res.status === 422 && data?.code === 'moderation_blocked') {
      // No credits are consumed on a moderation block; requestId lets support
      // trace the rejection with OpenAI without surfacing it to the user.
      try {
        Sentry.captureMessage('remove-background moderation_blocked', {
          level: 'info',
          extra: { requestId: data?.requestId },
        });
      } catch (_) {
        // telemetry must never break the user flow
      }
      const moderationError = new Error(
        'This image was blocked by the content moderation system. '
        + 'It may contain sexual, violent, or otherwise sensitive content that cannot be processed. '
        + 'Please try a different photo.',
      );
      moderationError.code = 'moderation_blocked';
      throw moderationError;
    }

    if (res.status === 400) {
      throw new Error(data?.error || 'Invalid image upload. Please check the file and try again.');
    }

    if (res.status === 401) {
      throw new Error('Your session has expired. Please sign in again.');
    }

    if (res.status === 403) {
      throw new Error('You are out of credits. Please add credits to keep using AI tools.');
    }

    throw new Error('Something went wrong while removing the background. Please try again.');
  }

  const pngBlob = await res.blob();

  return new File([ pngBlob ], 'removed-background.png', {
    type: 'image/png',
  });
};

const parseErrorMessage = async (res, fallback) => {
  try {
    const data = await res.json();
    if (data?.error) return data.error;
    if (data?.message) return data.message;
  } catch (_) {
    // response was not JSON
  }
  return fallback;
};

export const startMeshyImageTo3D = async ({ files, token }) => {
  const list = Array.isArray(files) ? files.filter(Boolean) : [];

  if (list.length === 0) {
    throw new Error('Please upload an image first.');
  }

  if (list.length > 4) {
    throw new Error('You can send up to 4 images.');
  }

  for (const f of list) {
    if (!ALLOWED_IMAGE_TYPES.includes(f.type)) {
      throw new Error('Unsupported file type. Please use PNG, JPG, JPEG, or WEBP.');
    }
  }

  const uploadFiles = await Promise.all(list.map(f => optimizeImageBeforeUpload(f)));

  const fd = new FormData();
  uploadFiles.forEach((f, idx) => {
    fd.append(`image_${idx}`, f, f.name || `image-${idx + 1}.png`);
  });

  const res = await fetch(`${VITE_API_DEV_HOST}/api/ai/meshy/image-to-3d/start`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: fd,
  });

  if (!res.ok) {
    if (res.status === 401) throw new Error('Your session has expired. Please sign in again.');
    if (res.status === 403) throw new Error('You do not have access to start a 3D generation.');
    const message = await parseErrorMessage(res, 'Failed to start 3D generation.');
    throw new Error(message);
  }

  return res.json();
};

export const downloadGlbFromUrl = async url => {
  if (!url) throw new Error('No model URL provided.');

  const res = await fetch(url);
  if (!res.ok) throw new Error('Could not download the generated model.');

  const blob = await res.blob();
  return new File([ blob ], 'meshy-generated-model.glb', {
    type: blob.type || 'model/gltf-binary',
  });
};

export const getMeshyImageTo3DStatus = async ({ taskId, token }) => {
  if (!taskId) throw new Error('Missing task id.');

  const res = await fetch(`${VITE_API_DEV_HOST}/api/ai/meshy/image-to-3d/${taskId}/status`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    if (res.status === 401) throw new Error('Your session has expired. Please sign in again.');
    if (res.status === 403) throw new Error('You do not have access to this task.');
    if (res.status === 404) throw new Error('Task was not found. Please restart generation.');
    const message = await parseErrorMessage(res, 'Failed to retrieve 3D generation status.');
    throw new Error(message);
  }

  return res.json();
};

export const makeImagePhotorealistic = async ({ file, token }) => {
  if (!file) {
    throw new Error('Please upload an image first.');
  }

  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    throw new Error('Unsupported file type. Please use PNG, JPG, JPEG, or WEBP.');
  }

  const uploadFile = await optimizeImageBeforeUpload(file);

  const fd = new FormData();
  fd.append('image', uploadFile);

  const res = await fetch(`${VITE_API_DEV_HOST}/api/ai/make-photorealistic`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: fd,
  });

  if (!res.ok) {
    let message = 'Failed to enhance image.';
    try {
      const data = await res.json();
      if (data?.error) message = data.error;
    } catch (_) {
      // response was not JSON
    }
    throw new Error(message);
  }

  const pngBlob = await res.blob();

  return new File([ pngBlob ], 'photorealistic.png', {
    type: 'image/png',
  });
};
